import type { TrpcHistoryData } from '@/utils/mapHistoryData';
import { useMemo, useState } from 'react';

import type { HistoryItem } from '@poseidon/components/ContactDetail';
import { interactionHistoryId } from '@poseidon/components/ContactDetail';

import { api } from '@/utils/api';
import { mapHistoryData } from '@/utils/mapHistoryData';

/** How many older entries each "Load older" fetches (the route caps a page at 200). */
export const OLDER_HISTORY_PAGE = 100;

interface OlderPageResult {
  data?: { data: TrpcHistoryData };
  isPending: boolean;
  isError: boolean;
  isFetching: boolean;
  refetch: () => unknown;
}

/**
 * The loaded older pages, folded once per change: tanstack re-runs a stable `combine` only when a page's result
 * changes, so the merged history keeps its identity across renders.
 */
function combineOlderPages(results: OlderPageResult[]) {
  const last = results[results.length - 1];
  return {
    // Each page oldest first; page 0 sits right before the panel's page, later pages further back.
    pages: results.map((r) => mapHistoryData(r.data?.data)),
    // A first load, or the retry of a page that failed.
    loading: results.some((r) => r.isPending || (r.isError && r.isFetching)),
    lastPageFull: last?.data != null && last.data.data.length >= OLDER_HISTORY_PAGE,
    lastPageFailed: last?.isError === true,
    retryLastPage: last?.refetch,
  };
}

/**
 * The contact's history past the newest page the contact panel loads (`history.contact` with the route's
 * default page) — fetched a page at a time on demand, for the Graph view's "Load older". Owned by the contact
 * page rather than the canvas, so toggling List / Graph keeps what the rep loaded; and kept per contact, so
 * coming back to one keeps its pages too. Returns the whole loaded history, oldest first, and whether the
 * server may hold anything older still.
 */
export function useOlderContactHistory({
  contactId,
  newest,
  newestHasOlder,
}: {
  contactId: string;
  /** The panel's page — the newest entries, oldest first. */
  newest: HistoryItem[];
  /** The panel's page came back full, so there may be more before it. */
  newestHasOlder: boolean;
}) {
  const [pagesByContact, setPagesByContact] = useState<Partial<Record<string, number>>>({});
  const pages = pagesByContact[contactId] ?? 0;
  const older = api.useQueries(
    (t) =>
      Array.from({ length: pages }, (_, k) =>
        t.history.contact(
          {
            contactId,
            filter: { invert: true },
            pagination: { limit: OLDER_HISTORY_PAGE, offset: newest.length + k * OLDER_HISTORY_PAGE },
          },
          // One quiet retry; past that the button offers it, rather than a long silent stall.
          { retry: 1 },
        ),
      ),
    { combine: combineOlderPages },
  );

  const items = useMemo(() => {
    if (older.pages.length === 0) return newest;
    // New activity shifts every offset down, so a page can repeat an entry the next one holds — keep one.
    const seen = new Set<string>();
    return [...[...older.pages].reverse().flat(), ...newest].filter((entry) => {
      const id = interactionHistoryId(entry);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [older.pages, newest]);

  return {
    items,
    // While a page loads it may still be full, and one that failed may be too — keep offering it either way.
    hasOlder: pages === 0 ? newestHasOlder : older.loading || older.lastPageFull || older.lastPageFailed,
    loadingOlder: older.loading,
    /** The last page failed to load — "Load older" retries it rather than asking for the next. */
    olderFailed: older.lastPageFailed,
    loadOlder: () => {
      if (older.lastPageFailed) {
        void older.retryLastPage?.();
        return;
      }
      setPagesByContact((m) => ({ ...m, [contactId]: (m[contactId] ?? 0) + 1 }));
    },
  };
}
